import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import TwoFactorModal from '../components/TwoFactorModal';
import use2faProtect from '../hooks/use2faProtect';
import { useAuth } from '../contexts/AuthContext';

const CATEGORY_LABELS = {
  general: 'General Settings',
  payment: 'Payment Settings',
  email: 'Email Settings',
  notification: 'Notification Settings',
  security: 'Security Settings',
  integration: 'Integrations',
};

const CATEGORY_ORDER = ['general', 'payment', 'email', 'notification', 'security', 'integration'];

const AdminSettings = () => {
  const { user } = useAuth();
  const { protect, show2fa, error2fa, handleVerify, handleClose } = use2faProtect();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedValues, setEditedValues] = useState({});

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getAdminSettings();
      if (res.data?.success) {
        setSettings(res.data.data.settings || {});
      }
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleChange = (key, value) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveCategory = async (category) => {
    const categorySettings = settings[category] || [];
    const changes = categorySettings
      .filter(s => editedValues[s.setting_key] !== undefined)
      .map(s => ({ setting_key: s.setting_key, setting_value: editedValues[s.setting_key] }));

    if (changes.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(category);
    try {
      await protect(async (headers) => {
        await adminAPI.updateAdminSettings(changes, headers);
        toast.success('Settings saved');
        // Clear edits for saved keys
        const newEdited = { ...editedValues };
        changes.forEach(c => delete newEdited[c.setting_key]);
        setEditedValues(newEdited);
        fetchSettings();
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(null);
    }
  };

  const getValue = (s) => {
    return editedValues[s.setting_key] !== undefined ? editedValues[s.setting_key] : s.setting_value;
  };

  const hasChanges = (category) => {
    const categorySettings = settings[category] || [];
    return categorySettings.some(s => editedValues[s.setting_key] !== undefined);
  };

  const renderInput = (s) => {
    const value = getValue(s);
    const disabled = s.is_sensitive && !isSuperAdmin;

    if (s.setting_type === 'boolean') {
      return (
        <label style={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => handleChange(s.setting_key, e.target.checked ? 'true' : 'false')}
            disabled={disabled}
          />
          <span>{value === 'true' ? 'Enabled' : 'Disabled'}</span>
        </label>
      );
    }

    if (s.is_sensitive) {
      return (
        <input
          style={styles.input}
          type="password"
          value={value || ''}
          onChange={(e) => handleChange(s.setting_key, e.target.value)}
          disabled={disabled}
          placeholder={disabled ? 'Restricted' : 'Enter value'}
        />
      );
    }

    return (
      <input
        style={styles.input}
        type={s.setting_type === 'number' ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => handleChange(s.setting_key, e.target.value)}
        disabled={disabled}
      />
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: 40, textAlign: 'center', color: '#6C757D' }}>Loading settings...</div>
      </AdminLayout>
    );
  }

  const categories = CATEGORY_ORDER.filter(c => settings[c]?.length > 0);

  return (
    <AdminLayout>
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Admin Settings</h1>
          <p style={styles.subtitle}>
            Configure application settings.
            {!isSuperAdmin && ' Some settings require super admin access.'}
          </p>
        </div>

        {categories.length === 0 ? (
          <div style={styles.card}>
            <p style={{ color: '#6C757D' }}>No settings available. Run database migrations to create settings.</p>
          </div>
        ) : (
          categories.map(category => (
            <div key={category} style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>{CATEGORY_LABELS[category] || category}</h2>
                <button
                  style={{
                    ...styles.btnPrimary,
                    opacity: hasChanges(category) ? 1 : 0.5,
                  }}
                  onClick={() => handleSaveCategory(category)}
                  disabled={!hasChanges(category) || saving === category}
                >
                  {saving === category ? 'Saving...' : 'Save'}
                </button>
              </div>

              {(settings[category] || []).map(s => (
                <div key={s.setting_key} style={styles.formRow}>
                  <div style={styles.formLabel}>
                    <span style={styles.labelText}>
                      {s.description || s.setting_key}
                      {s.is_sensitive && (
                        <span style={styles.sensitiveTag}>sensitive</span>
                      )}
                    </span>
                    <span style={styles.keyText}>{s.setting_key}</span>
                  </div>
                  <div style={styles.formControl}>
                    {renderInput(s)}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      <TwoFactorModal
        isOpen={show2fa}
        onClose={handleClose}
        onVerify={handleVerify}
        error={error2fa}
      />
    </AdminLayout>
  );
};

const styles = {
  page: { padding: '24px', maxWidth: '800px' },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 700, color: '#212529', margin: '0 0 4px' },
  subtitle: { color: '#6C757D', margin: 0, fontSize: '14px' },
  card: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid #E9ECEF',
  },
  cardTitle: { fontSize: '18px', fontWeight: 600, margin: 0, color: '#212529' },
  formRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #F0F0F0',
    gap: '16px',
    flexWrap: 'wrap',
  },
  formLabel: { flex: '1 1 250px', minWidth: 0 },
  labelText: { display: 'block', fontSize: '14px', fontWeight: 500, color: '#495057' },
  keyText: { display: 'block', fontSize: '11px', color: '#ADB5BD', marginTop: 2 },
  sensitiveTag: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 600,
    color: '#DC3545',
    background: 'rgba(220,53,69,0.08)',
    padding: '1px 6px',
    borderRadius: '4px',
    marginLeft: '6px',
    textTransform: 'uppercase',
  },
  formControl: { flex: '0 0 280px' },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #CED4DA',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  btnPrimary: {
    padding: '8px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#0D6EFD',
    color: '#FFF',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '14px',
    flexShrink: 0,
  },
};

export default AdminSettings;
